import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import "./table.css";

import {
  useFileUpload,
  CATEGORY_OPTIONS,
  DEFAULT_CATEGORY,
  API_ENDPOINTS,
} from "./components/repository";

const RepositoryAddLink = forwardRef((props, ref) => {
  const getEffectiveReportName = () => {
    const reportName = localStorage.getItem("report_name");
    const effectiveName = reportName && reportName !== "undefined" ? reportName : "Repository";
    if (!reportName || reportName === "undefined") {
      localStorage.setItem("report_name", effectiveName);
    }
    return effectiveName;
  };

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    linkName: "",
    linkCategory: "",
    url: "",
    fileCategory: DEFAULT_CATEGORY,
    itemType: "file",
    file: null,
  });
  const [errors, setErrors] = useState({});

  const { uploadFile, uploading, progress } = useFileUpload({
    onSuccess: (result) => {
      setLoading(false);
      if (props.onClose) props.onClose();
      Swal.fire({
        width: 400,
        heightAuto: false,
        position: "center",
        icon: "success",
        html: `<h4 style="color:#407775;font-size: 20px;">File uploaded successfully</h4><p style="font-size:16px;">Your file is being analyzed for AI generation</p>`,
        iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
        confirmButtonText: "Continue",
        scrollbarPadding: false,
      }).then((result) => {
        if (result.isConfirmed && props.onSuccess) {
          props.onSuccess();
        }
      });
    },
    onError: (err) => {
      setLoading(false);
      Swal.fire({
        icon: "error",
        position: "center",
        html: `<h4 style="color:#CC2229;font-size: 20px;">Error!</h4><p style="font-size:16px;">${err.message || "Failed to upload file"}</p>`,
        iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
        scrollbarPadding: false,
        confirmButtonText: "Close",
      });
    },
  });

  useEffect(() => {
    getEffectiveReportName();
  }, []);

  const validateForm = () => {
    let formErrors = {};

    if (formData.itemType === "file") {
      // Category is optional - will use default
      if (!formData.file) {
        formErrors.file = "Please upload a file";
      }
    } else if (formData.itemType === "link") {
      if (!formData.linkName.trim()) formErrors.linkName = "Please enter a name for the link";
      if (!formData.linkCategory) formErrors.linkCategory = "Please select a category";
      if (!formData.url.trim()) {
        formErrors.url = "Please enter a URL";
      } else {
        // Duplicate URL check against existing links
        const existingLinks = Array.isArray(props.existingLinks) ? props.existingLinks : [];
        const normalizedUrl = formData.url.trim().toLowerCase();
        const isDuplicate = existingLinks.some(
          (link) => (link.Link || link["File Link"] || link.url || "").toLowerCase() === normalizedUrl
        );
        if (isDuplicate) {
          formErrors.url = "This URL has already been added to the repository.";
        }
      }
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (formData.itemType === "file") {
      setLoading(true);
      const category = formData.fileCategory || DEFAULT_CATEGORY;
      await uploadFile(formData.file, category);
    } else if (formData.itemType === "link") {
      setLoading(true);
      const companyid = localStorage.getItem("companyid");
      const username = localStorage.getItem("userName");
      const effectiveReportName = getEffectiveReportName();

      try {
        // Step 1 — scrape link into Azure Blob as .md file
        const addLinksResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.ADD_LINKS}`, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            username: username,
          },
          body: JSON.stringify({
            company_id: companyid,
            report_name: effectiveReportName,
            report_type: "csrd",
            links: [formData.url],
            link_metadata: [{ url: formData.url, name: formData.linkName, category: formData.linkCategory }],
          }),
        });

        if (addLinksResponse.status !== 200) {
          setLoading(false);
          Swal.fire({
            icon: "error",
            position: "center",
            html: `<h4 style="color:#CC2229;font-size: 20px;">Error!</h4><p style="font-size:16px;">Failed to submit link. Please try again.</p>`,
            iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
            scrollbarPadding: false,
            confirmButtonText: "Close",
          });
          return;
        }

        const addLinksText = await addLinksResponse.text();
        const addLinksJson = addLinksText ? JSON.parse(addLinksText) : {};
        const taskId = addLinksJson.task_id;

        // Step 2 — poll until scraping is complete (only if backend gave us a task_id to track)
        let scrapingTimedOut = false;
        const completedStatus = taskId ? await new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 120; // 120 × 5s = 10 minutes
          const qs = new URLSearchParams();
          if (effectiveReportName) qs.set("report_name", effectiveReportName);
          if (taskId) qs.set("task_id", taskId);
          const qsStr = qs.toString() ? `?${qs.toString()}` : "";
          const interval = setInterval(async () => {
            attempts++;
            try {
              const statusResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.GET_REPO_STATUS}/${companyid}${qsStr}`);
              const statusJson = await statusResponse.json();
              if (statusJson.stage === "Completed") {
                clearInterval(interval);
                resolve(statusJson);
              } else if (statusJson.stage === "Failed") {
                clearInterval(interval);
                reject(new Error(statusJson.message || "Scraping failed. Please try again."));
              } else if (attempts >= maxAttempts) {
                clearInterval(interval);
                scrapingTimedOut = true;
                resolve(null); // link was saved — close gracefully
              }
            } catch (err) {
              clearInterval(interval);
              reject(err);
            }
          }, 5000);
        }) : null; // no task_id — scraping is async, proceed to training

        const scrapedBlobName = completedStatus?.scraped_blob_names?.[0] || null;

        // Step 3 — trigger training on the new blob
        const trainResponse = await fetch(`${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.ADD_FILES}`, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            username: username,
          },
          body: JSON.stringify({
            company_id: companyid,
            report_name: effectiveReportName,
            report_type: "csrd",
            ...(scrapedBlobName && { file_name: scrapedBlobName }),
          }),
        });

        if (!scrapingTimedOut) {
          if (!trainResponse.ok) {
            throw new Error("Training request failed. The link was saved but analysis may not have started.");
          }
        }

        setLoading(false);
        if (props.onClose) props.onClose();
        const successMessage = scrapingTimedOut
          ? `<h4 style="color:#407775;font-size: 20px;">Link saved</h4><p style="font-size:16px;">Analysis is running in the background. The status will update automatically.</p>`
          : `<h4 style="color:#407775;font-size: 20px;">Link successfully submitted</h4><p style="font-size:16px;">Analysis triggered</p>`;
        Swal.fire({
          width: 400,
          heightAuto: false,
          position: "center",
          icon: "success",
          html: successMessage,
          iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
          confirmButtonText: "Continue",
          scrollbarPadding: false,
        }).then(() => {
          if (props.onSuccess) props.onSuccess();
        });
      } catch (error) {
        console.error(error);
        setLoading(false);
        Swal.fire({
          icon: "error",
          position: "center",
          html: `<h4 style="color:#CC2229;font-size: 20px;">Error!</h4><p style="font-size:16px;">${error.message || "An unexpected error occurred. Please try again."}</p>`,
          iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
          scrollbarPadding: false,
          confirmButtonText: "Close",
        });
      }
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSave,
  }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, file });
    if (file) {
      setErrors((prevErrors) => ({ ...prevErrors, file: "" }));
    }
  };

  const handleFileDelete = () => {
    setFormData({ ...formData, file: null });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.remove("drag-over");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.remove("drag-over");

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setFormData({ ...formData, file: files[0] });
      setErrors((prevErrors) => ({ ...prevErrors, file: "" }));
    }
  };

  return (
    <div className="form-part add-repo">
      <div>
        {(loading || uploading) && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>

      <form className="edit-form">
        <div className="row mb-4">
          <div className="col">
            <div className="flex flex-wrap gap-3">
              <div className="flex align-items-center">
                <RadioButton inputId="fileRadio" name="itemType" value="file" checked={formData.itemType === "file"} onChange={(e) => setFormData({ ...formData, itemType: e.value })} />
                <label htmlFor="fileRadio" className="ml-2 mb-0 formlable">
                  File
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton inputId="linkRadio" name="itemType" value="link" checked={formData.itemType === "link"} onChange={(e) => setFormData({ ...formData, itemType: e.value })} />
                <label htmlFor="linkRadio" className="ml-2 mb-0 formlable">
                  Link
                </label>
              </div>
            </div>
          </div>
        </div>

        {formData.itemType === "file" && (
          <div className="row file-row g-5">
            <div className="col-md-12">
              <div className="form-group">
                <label className="formlable" htmlFor="fileCategory">
                  Category
                </label>
                <Dropdown
                  id="fileCategory"
                  className="form-control wd-100"
                  value={formData.fileCategory}
                  options={CATEGORY_OPTIONS}
                  placeholder="Select category (optional)"
                  appendTo={document.body}
                  showClear
                  virtualScrollerOptions={{ itemSize: 38 }}
                  onChange={(e) => {
                    setFormData({ ...formData, fileCategory: e.value || DEFAULT_CATEGORY });
                  }}
                />
                <small className="form-text text-muted">Defaults to "General" if not selected</small>
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="fileUpload">
                  Upload File<span className="mark-form">*</span>
                </label>
                <label htmlFor="fileUpload" className="file-upload-box" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                  <input type="file" id="fileUpload" onChange={handleFileChange} className="file-input" />
                  <div className="upload-icon">
                    <img src="./assets/img/reports/upload-file.svg" alt="Upload" />
                  </div>
                  <p>
                    <span>Browse</span> or drag and drop here
                  </p>
                </label>

                {formData.file && (
                  <div className="file-preview">
                    <span className="file-name">{formData.file.name}</span>
                    <Tooltip title="Delete" arrow>
                      <img src="./assets/img/reports/delete-repo.svg" alt="Delete Icon" className="delete-icon actions-icon" onClick={handleFileDelete} />
                    </Tooltip>
                  </div>
                )}

                {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}

                {uploading && (
                  <div className="mt-2">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${progress}%`, backgroundColor: "#407775" }}
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {progress}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {formData.itemType === "link" && (
          <div className="row link-row g-5">
            <div className="col-md-12">
              <div className="form-group">
                <label className="formlable" htmlFor="linkName">
                  Name of the Link<span className="mark-form">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.linkName ? "is-invalid" : ""}`}
                  id="linkName"
                  placeholder="Enter name of the link"
                  value={formData.linkName}
                  onChange={(e) => {
                    setFormData({ ...formData, linkName: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, linkName: "" }));
                  }}
                />
                {errors.linkName && <div className="invalid-feedback">{errors.linkName}</div>}
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="linkCategory">
                  Category<span className="mark-form">*</span>
                </label>
                <Dropdown
                  id="linkCategory"
                  className={`form-control wd-100 ${errors.linkCategory ? "is-invalid" : ""}`}
                  value={formData.linkCategory}
                  options={CATEGORY_OPTIONS}
                  placeholder="Select category"
                  appendTo={document.body}
                  showClear
                  virtualScrollerOptions={{ itemSize: 38 }}
                  onChange={(e) => {
                    setFormData({ ...formData, linkCategory: e.value });
                    setErrors((prevErrors) => ({ ...prevErrors, linkCategory: "" }));
                  }}
                />
                {errors.linkCategory && <div className="invalid-feedback d-block">{errors.linkCategory}</div>}
              </div>

              <div className="form-group">
                <label className="formlable" htmlFor="url">
                  URL<span className="mark-form">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.url ? "is-invalid" : ""}`}
                  id="url"
                  placeholder="Enter URL"
                  value={formData.url}
                  onChange={(e) => {
                    setFormData({ ...formData, url: e.target.value });
                    setErrors((prevErrors) => ({ ...prevErrors, url: "" }));
                  }}
                />
                {errors.url && <div className="invalid-feedback">{errors.url}</div>}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
});

export default RepositoryAddLink;
